import LiveChat from "components/chat/live-chat";
import { ChangeEvent, FormEvent, useMemo } from "react";
import { useEvent } from "utils/use-event";
import { useChat } from "ai/react";
import "./agent-chat-view.scss";
import { TProduct, useCart } from "components/cart/cart-provider";
import CartList from "components/cart/cart-list";
import { Recipe } from "types/recipe";
import { performProductSearch, performRecipeSearch } from "utils/search-utils";

type TMessages = { role: "user" | "assistant"; content: string; id: string }[];

const emptyArr: TMessages = [];

const item = { messages: [] as any[], title: "123" };

const AgentChatView = ({
  chatId,
  onSearch,
  onSearchRecipe,
}: {
  chatId: string;
  onSearch: (
    query: string,
    options: { serviceId: string }
  ) => Promise<TProduct[]> | TProduct[];
  onSearchRecipe: (
    query: string,
    options: { serviceId: string }
  ) => Promise<Recipe[]>;
}) => {
  const {
    addToCart,
    decreaseQuantity,
    itemList,
    setSuggestedRecipes,
    setSearchQuery,
    ...cart
  } = useCart();

  const chatLoading = false;

  const initialMessages = useMemo(() => {
    return (
      item?.messages?.map((msg, index) => ({
        id: msg.role + index,
        ...msg,
      })) || emptyArr
    );
  }, []);

  const {
    messages,
    handleSubmit,
    handleInputChange,
    input,
    isLoading,
    stop,
    append,
  } = useChat({
    id: chatId,
    initialMessages: initialMessages,
    api:
      `${process.env.REACT_APP_API_URL}/api/message` +
      (chatId ? `?chatId=${chatId}` : ""),
    async onToolCall(props) {
      if (props.toolCall.toolName === "search") {
        const args = props.toolCall.args as {
          items: { name: string }[];
          originalQuery: string;
        };
        const items = args.items || [];

        // Set the search query for diet type detection
        setSearchQuery(args.originalQuery);

        const newCart = await performProductSearch({
          items,
          context: args.originalQuery,
          searchFunction: onSearch,
          serviceId: cart.serviceId,
        });

        addToCart(newCart);
        cart.setStep("cart");
      }

      if (props.toolCall.toolName === "search_recipe") {
        const args = props.toolCall.args as {
          item: string;
          originalQuery: string;
        };

        // Set the search query for diet type detection
        setSearchQuery(args.originalQuery);

        // Search for recipes using the extracted function
        const item = args.item;
        if (item) {
          const recipes = await performRecipeSearch({
            query: item,
            context: args.originalQuery,
            searchFunction: onSearchRecipe,
            options: { serviceId: cart.serviceId },
          });
          setSuggestedRecipes(recipes);
        }

        cart.setStep("recipe");
      }

      if (props.toolCall.toolName === "remove") {
        const items =
          (
            props.toolCall.args as {
              items: { id: string; name: string; quantity: string }[];
            }
          ).items || [];

        items.forEach((item) => {
          decreaseQuantity(item.id, parseInt(item.quantity) || 1);
        });
      }

      return "Your cart has been updated";
    },
    body: {
      cart: itemList.map(({ options, ...rest }) => ({ ...rest })),
    },
  });

  const onInputChange = useEvent(
    async (event: ChangeEvent<HTMLInputElement>) => {
      handleInputChange(event);
    }
  );

  const onTranscription = useEvent((transcription: string) => {
    append({ role: "user", content: transcription });
  });

  const onSubmit = useEvent(async (event: FormEvent<HTMLFormElement>) => {
    handleSubmit(event);
  });

  return (
    <div
      className={`${cart.serviceId}`}
      style={{ width: "100%", height: "100%" }}
    >
      <LiveChat
        serviceId={cart.serviceId}
        onSearch={onSearch}
        onSearchRecipe={onSearchRecipe}
        onTranscription={onTranscription}
        meta={<CartList />}
        chatLoading={chatLoading}
        isLoading={isLoading}
        input={input}
        messages={messages as TMessages}
        onSubmit={onSubmit}
        onInputChange={onInputChange}
        onStop={stop}
        showRecipePanel={true}
      />
    </div>
  );
};

export default AgentChatView;
